import React, { useState, useEffect } from 'react';
import {
    Box, Button, Paper, Typography, CircularProgress, Alert, Snackbar,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Grid, Switch, FormControlLabel, Chip, useMediaQuery, useTheme
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import {
    getSubscriptionTiersV2, updateSubscriptionTierV2, createSubscriptionTierV2
} from '../../api/admin';
import logger from '../../utils/logger';
import './AdminView.css';

const SubscriptionSettingsTab = ({ auth }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [tiers, setTiers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Edit/Create Dialog State
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingTier, setEditingTier] = useState(null); // null for create mode
    const [formData, setFormData] = useState({
        id: '',
        name: '',
        description: '',
        priceMonthly: 0,
        priceYearly: 0,
        dailyStaticValidations: 5,
        dailyAiValidations: 0,
        dailyWebhookCalls: 10,
        maxStaticRules: 5,
        maxAiRules: 0,
        maxPipelinesPerInstrument: 1,
        // Analytics limits
        maxAnalyticsPipelines: 1,
        maxAnalyticsModules: 3,
        dailyAnalyticsExecutions: 10,
        displayOrder: 0,
        isActive: true,
        features: '{}'
    });

    const loadTiers = async () => {
        setLoading(true);
        try {
            await getSubscriptionTiersV2(auth, (response) => {
                if (Array.isArray(response)) {
                    setTiers(response);
                } else if (response.data) {
                    setTiers(response.data);
                }
            });
        } catch (error) {
            logger.error('Error loading tiers:', error);
            setSnackbar({ open: true, message: 'Failed to load tiers', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadTiers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth]);

    const handleEditClick = (tier) => {
        setEditingTier(tier);
        setFormData({
            id: tier.id,
            name: tier.name,
            description: tier.description,
            priceMonthly: tier.priceMonthly,
            priceYearly: tier.priceYearly,
            dailyStaticValidations: tier.dailyStaticValidations,
            dailyAiValidations: tier.dailyAiValidations,
            dailyWebhookCalls: tier.dailyWebhookCalls !== undefined ? tier.dailyWebhookCalls : 10,
            maxStaticRules: tier.maxStaticRules !== undefined ? tier.maxStaticRules : 5,
            maxAiRules: tier.maxAiRules !== undefined ? tier.maxAiRules : 0,
            maxPipelinesPerInstrument: tier.maxPipelinesPerInstrument !== undefined ? tier.maxPipelinesPerInstrument : 1,
            // Analytics limits
            maxAnalyticsPipelines: tier.maxAnalyticsPipelines !== undefined ? tier.maxAnalyticsPipelines : 1,
            maxAnalyticsModules: tier.maxAnalyticsModules !== undefined ? tier.maxAnalyticsModules : 3,
            dailyAnalyticsExecutions: tier.dailyAnalyticsExecutions !== undefined ? tier.dailyAnalyticsExecutions : 10,
            displayOrder: tier.displayOrder,
            isActive: tier.isActive,
            features: typeof tier.features === 'object' ? JSON.stringify(tier.features, null, 2) : tier.features
        });
        setDialogOpen(true);
    };

    const handleCreateClick = () => {
        setEditingTier(null);
        setFormData({
            id: '',
            name: '',
            description: '',
            priceMonthly: 0,
            priceYearly: 0,
            dailyStaticValidations: 5,
            dailyAiValidations: 0,
            dailyWebhookCalls: 10,
            maxStaticRules: 5,
            maxAiRules: 0,
            maxPipelinesPerInstrument: 1,
            // Analytics limits
            maxAnalyticsPipelines: 1,
            maxAnalyticsModules: 3,
            dailyAnalyticsExecutions: 10,
            displayOrder: tiers.length + 1,
            isActive: true,
            features: '{\n  "staticRules": true,\n  "llmRules": false,\n  "maxRulesInPipeline": 5\n}'
        });
        setDialogOpen(true);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            // Validate JSON
            let featuresJson;
            try {
                featuresJson = JSON.parse(formData.features);
            } catch (e) {
                setSnackbar({ open: true, message: 'Invalid JSON in features', severity: 'error' });
                setSaving(false);
                return;
            }

            const payload = {
                ...formData,
                priceMonthly: parseFloat(formData.priceMonthly),
                priceYearly: parseFloat(formData.priceYearly),
                dailyStaticValidations: parseInt(formData.dailyStaticValidations),
                dailyAiValidations: parseInt(formData.dailyAiValidations),
                dailyWebhookCalls: parseInt(formData.dailyWebhookCalls),
                maxStaticRules: parseInt(formData.maxStaticRules),
                maxAiRules: parseInt(formData.maxAiRules),
                maxPipelinesPerInstrument: parseInt(formData.maxPipelinesPerInstrument),
                // Analytics limits
                maxAnalyticsPipelines: parseInt(formData.maxAnalyticsPipelines),
                maxAnalyticsModules: parseInt(formData.maxAnalyticsModules),
                dailyAnalyticsExecutions: parseInt(formData.dailyAnalyticsExecutions),
                displayOrder: parseInt(formData.displayOrder),
                features: JSON.stringify(featuresJson) // Send as string or object depending on API expectation?
                // Controller expects String features = request.get("features").toString();
            };

            if (editingTier) {
                await updateSubscriptionTierV2(auth, (result) => {
                    if (result && !result.error) {
                        setSnackbar({ open: true, message: 'Tier updated successfully', severity: 'success' });
                        setDialogOpen(false);
                        loadTiers();
                    } else {
                        setSnackbar({ open: true, message: result?.error || 'Failed to update tier', severity: 'error' });
                    }
                }, editingTier.id, payload);
            } else {
                await createSubscriptionTierV2(auth, (result) => {
                    if (result && !result.error) {
                        setSnackbar({ open: true, message: 'Tier created successfully', severity: 'success' });
                        setDialogOpen(false);
                        loadTiers();
                    } else {
                        setSnackbar({ open: true, message: result?.error || 'Failed to create tier', severity: 'error' });
                    }
                }, payload);
            }
        } catch (error) {
            logger.error('Error saving tier:', error);
            setSnackbar({ open: true, message: 'Error saving tier', severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    return (
        <Box className="admin-tab-content">
            {/* Header */}
            <Box className="admin-section-header">
                <Typography variant="h6">Subscription Tiers</Typography>
                <Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleCreateClick}
                        size="small"
                        sx={{ mr: 1 }}
                    >
                        Create Tier
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={loadTiers}
                        startIcon={<RefreshIcon />}
                        size="small"
                        disabled={loading}
                    >
                        Refresh
                    </Button>
                </Box>
            </Box>

            {/* Tiers List */}
            <Paper className="admin-settings-card">
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {/* Mobile Card View */}
                        {isMobile && (
                            <div className="admin-cards-mobile">
                                {tiers.map((tier) => (
                                    <div className="admin-mobile-card" key={tier.id}>
                                        <div className="admin-mobile-card-header">
                                            <div>
                                                <Typography variant="subtitle1" className="admin-mobile-card-title">
                                                    {tier.name}
                                                </Typography>
                                                <Chip label={tier.id} size="small" variant="outlined" />
                                            </div>
                                            {tier.isActive ? (
                                                <CheckCircleIcon color="success" fontSize="small" />
                                            ) : (
                                                <CancelIcon color="disabled" fontSize="small" />
                                            )}
                                        </div>
                                        <div className="admin-mobile-card-body">
                                            <div className="admin-mobile-card-row">
                                                <span className="admin-mobile-card-row-label">Price</span>
                                                <span className="admin-mobile-card-row-value">
                                                    ${tier.priceMonthly}/mo - ${tier.priceYearly}/yr
                                                </span>
                                            </div>
                                            <div className="admin-mobile-card-row">
                                                <span className="admin-mobile-card-row-label">Daily Validations</span>
                                                <div className="admin-mobile-card-chips">
                                                    <Chip
                                                        label={`Static: ${tier.dailyStaticValidations === -1 ? '∞' : tier.dailyStaticValidations}`}
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                    />
                                                    <Chip
                                                        label={`AI: ${tier.dailyAiValidations === -1 ? '∞' : tier.dailyAiValidations}`}
                                                        size="small"
                                                        color="secondary"
                                                        variant="outlined"
                                                    />
                                                </div>
                                            </div>
                                            <div className="admin-mobile-card-row">
                                                <span className="admin-mobile-card-row-label">Webhooks</span>
                                                <span className="admin-mobile-card-row-value">
                                                    {tier.dailyWebhookCalls === -1 ? '∞' : (tier.dailyWebhookCalls || 10)}
                                                </span>
                                            </div>
                                            <div className="admin-mobile-card-row">
                                                <span className="admin-mobile-card-row-label">Rules</span>
                                                <div className="admin-mobile-card-chips">
                                                    <Chip
                                                        label={`Static: ${tier.maxStaticRules === -1 ? '∞' : (tier.maxStaticRules || 5)}`}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                    <Chip
                                                        label={`AI: ${tier.maxAiRules === -1 ? '∞' : (tier.maxAiRules || 0)}`}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                </div>
                                            </div>
                                            <div className="admin-mobile-card-row">
                                                <span className="admin-mobile-card-row-label">Pipelines</span>
                                                <span className="admin-mobile-card-row-value">
                                                    {tier.maxPipelinesPerInstrument === -1 ? '∞' : (tier.maxPipelinesPerInstrument || 1)}
                                                </span>
                                            </div>
                                            <div className="admin-mobile-card-row">
                                                <span className="admin-mobile-card-row-label">Analytics</span>
                                                <div className="admin-mobile-card-chips">
                                                    <Chip
                                                        label={`Daily: ${tier.dailyAnalyticsExecutions === -1 ? '∞' : (tier.dailyAnalyticsExecutions || 10)}`}
                                                        size="small"
                                                        color="warning"
                                                        variant="outlined"
                                                    />
                                                    <Chip
                                                        label={`Modules: ${tier.maxAnalyticsModules === -1 ? '∞' : (tier.maxAnalyticsModules || 3)}`}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="admin-mobile-card-actions">
                                            <Button
                                                size="small"
                                                startIcon={<EditIcon />}
                                                onClick={() => handleEditClick(tier)}
                                            >
                                                Edit
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Desktop Table View */}
                        {!isMobile && (
                            <div className="admin-table-desktop">
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Order</TableCell>
                                                <TableCell>Name</TableCell>
                                                <TableCell>ID</TableCell>
                                                <TableCell>Price (M/Y)</TableCell>
                                                <TableCell>Daily Validations</TableCell>
                                                <TableCell>Webhooks</TableCell>
                                                <TableCell>Rules (Static/AI)</TableCell>
                                                <TableCell>Pipelines</TableCell>
                                                <TableCell>Analytics</TableCell>
                                                <TableCell>Status</TableCell>
                                                <TableCell align="right">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {tiers.map((tier) => (
                                                <TableRow key={tier.id}>
                                                    <TableCell>{tier.displayOrder}</TableCell>
                                                    <TableCell>
                                                        <Typography variant="subtitle2">{tier.name}</Typography>
                                                        <Typography variant="caption" color="textSecondary">
                                                            {tier.description}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip label={tier.id} size="small" variant="outlined" />
                                                    </TableCell>
                                                    <TableCell>
                                                        ${tier.priceMonthly} / ${tier.priceYearly}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            <Chip
                                                                label={`Static: ${tier.dailyStaticValidations === -1 ? '∞' : tier.dailyStaticValidations}`}
                                                                size="small"
                                                                color="primary"
                                                                variant="outlined"
                                                            />
                                                            <Chip
                                                                label={`AI: ${tier.dailyAiValidations === -1 ? '∞' : tier.dailyAiValidations}`}
                                                                size="small"
                                                                color="secondary"
                                                                variant="outlined"
                                                            />
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={tier.dailyWebhookCalls === -1 ? '∞' : (tier.dailyWebhookCalls || 10)}
                                                            size="small"
                                                            color="success"
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            <Chip
                                                                label={`Static: ${tier.maxStaticRules === -1 ? '∞' : (tier.maxStaticRules || 5)}`}
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                            <Chip
                                                                label={`AI: ${tier.maxAiRules === -1 ? '∞' : (tier.maxAiRules || 0)}`}
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={tier.maxPipelinesPerInstrument === -1 ? '∞' : (tier.maxPipelinesPerInstrument || 1)}
                                                            size="small"
                                                            color="info"
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                            <Chip
                                                                label={`Daily: ${tier.dailyAnalyticsExecutions === -1 ? '∞' : (tier.dailyAnalyticsExecutions || 10)}`}
                                                                size="small"
                                                                color="warning"
                                                                variant="outlined"
                                                            />
                                                            <Chip
                                                                label={`Modules: ${tier.maxAnalyticsModules === -1 ? '∞' : (tier.maxAnalyticsModules || 3)}`}
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        {tier.isActive ? (
                                                            <CheckCircleIcon color="success" fontSize="small" />
                                                        ) : (
                                                            <CancelIcon color="disabled" fontSize="small" />
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <IconButton size="small" onClick={() => handleEditClick(tier)}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </div>
                        )}
                    </>
                )}
            </Paper>

            {/* Edit/Create Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>{editingTier ? 'Edit Tier' : 'Create New Tier'}</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Tier ID"
                                value={formData.id}
                                onChange={(e) => handleChange('id', e.target.value)}
                                fullWidth
                                disabled={!!editingTier}
                                helperText="Unique identifier (e.g., 'pro', 'enterprise')"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Display Name"
                                value={formData.name}
                                onChange={(e) => handleChange('name', e.target.value)}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Description"
                                value={formData.description}
                                onChange={(e) => handleChange('description', e.target.value)}
                                fullWidth
                                multiline
                                rows={2}
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                label="Monthly Price ($)"
                                type="number"
                                value={formData.priceMonthly}
                                onChange={(e) => handleChange('priceMonthly', e.target.value)}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                label="Yearly Price ($)"
                                type="number"
                                value={formData.priceYearly}
                                onChange={(e) => handleChange('priceYearly', e.target.value)}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                label="Daily Static Validations"
                                type="number"
                                value={formData.dailyStaticValidations}
                                onChange={(e) => handleChange('dailyStaticValidations', e.target.value)}
                                fullWidth
                                helperText="-1 for unlimited"
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                label="Daily AI Validations"
                                type="number"
                                value={formData.dailyAiValidations}
                                onChange={(e) => handleChange('dailyAiValidations', e.target.value)}
                                fullWidth
                                helperText="-1 for unlimited"
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                label="Daily Webhook Calls"
                                type="number"
                                value={formData.dailyWebhookCalls}
                                onChange={(e) => handleChange('dailyWebhookCalls', e.target.value)}
                                fullWidth
                                helperText="-1 for unlimited"
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                label="Max Static Rules (Pipeline)"
                                type="number"
                                value={formData.maxStaticRules}
                                onChange={(e) => handleChange('maxStaticRules', e.target.value)}
                                fullWidth
                                helperText="-1 for unlimited"
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                label="Max AI Rules (Pipeline)"
                                type="number"
                                value={formData.maxAiRules}
                                onChange={(e) => handleChange('maxAiRules', e.target.value)}
                                fullWidth
                                helperText="-1 for unlimited"
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                label="Max Pipelines per Instrument"
                                type="number"
                                value={formData.maxPipelinesPerInstrument}
                                onChange={(e) => handleChange('maxPipelinesPerInstrument', e.target.value)}
                                fullWidth
                                helperText="-1 for unlimited"
                            />
                        </Grid>
                        {/* Analytics Limits */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mt: 1, mb: 1, color: 'text.secondary' }}>
                                Analytics Limits
                            </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                label="Max Analytics Pipelines"
                                type="number"
                                value={formData.maxAnalyticsPipelines}
                                onChange={(e) => handleChange('maxAnalyticsPipelines', e.target.value)}
                                fullWidth
                                helperText="-1 for unlimited"
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                label="Max Analytics Modules"
                                type="number"
                                value={formData.maxAnalyticsModules}
                                onChange={(e) => handleChange('maxAnalyticsModules', e.target.value)}
                                fullWidth
                                helperText="-1 for unlimited"
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                label="Daily Analytics Executions"
                                type="number"
                                value={formData.dailyAnalyticsExecutions}
                                onChange={(e) => handleChange('dailyAnalyticsExecutions', e.target.value)}
                                fullWidth
                                helperText="-1 for unlimited"
                            />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <TextField
                                label="Display Order"
                                type="number"
                                value={formData.displayOrder}
                                onChange={(e) => handleChange('displayOrder', e.target.value)}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.isActive}
                                        onChange={(e) => handleChange('isActive', e.target.checked)}
                                    />
                                }
                                label="Active"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Features (JSON)"
                                value={formData.features}
                                onChange={(e) => handleChange('features', e.target.value)}
                                fullWidth
                                multiline
                                rows={6}
                                sx={{ fontFamily: 'monospace' }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={saving}
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default SubscriptionSettingsTab;
